import { BottomDrawer, Button, Modal, Radio } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgKebab } from '@common/icon';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { AuthMutation, AuthQueryKey, UnauthQueryKey } from '@/api';
import { SELECTOR } from '@/constants';
import { useDeleteCommentMutation } from '@/hooks/useDeleteCommentMutation';

export interface CommentMenuProps {
  isMyComment: boolean;
  commentId: number;
  onClickEditButton?: (commentId: number) => void;
}

enum MODAL_TYPE {
  DELETE = 'DELETE',
  REPORT = 'REPORT',
}

const REPORT_REASONS = [
  '홍보성 광고글',
  '음란성, 선정성 글',
  '욕설',
  '개인정보 노출',
  '반복 게시글',
  '기타',
];

export const CommentMenu = ({
  isMyComment,
  commentId,
  onClickEditButton,
}: CommentMenuProps) => {
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const [isReportDrawerOpened, setIsReportDrawerOpened] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>();

  const closeAll = () => {
    setIsDrawerOpened(false);
    setIsModalOpened(false);
    setSelectedReason(undefined);
  };

  const { toast } = useToast();

  const [modalType, setModalType] = useState(MODAL_TYPE.DELETE);

  const postCommentReportMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/wello-comment-report'](),
    meta: {
      reset: [
        AuthQueryKey['get:/wello/v2/auth/wello-comment'](),
        UnauthQueryKey['get:/wello/v2/allows/wello-comment'](),
        AuthQueryKey['get:/wello/v2/auth/wello-comment/my'](),
      ],
    },
    onSuccess: async (_, { body }) => {
      const commentId = SELECTOR.COMMENT_PREFIX + body?.welloCommentId;

      scroller.scrollTo(String(commentId), {
        containerId: SELECTOR.MAIN,
        offset: -100,
      });

      closeAll();

      toast({
        message: '댓글이 신고되었습니다.',
      });
    },
  });

  const deleteCommentMutation = useDeleteCommentMutation();

  return (
    <>
      <Button
        className="flex size-26 items-center justify-center p-0"
        dimensions="H26"
        theme="sub-text"
        onClick={() => {
          setIsDrawerOpened(true);
        }}
      >
        <SvgKebab className="size-14 text-Gray-700" />
      </Button>
      <BottomDrawer
        opened={isDrawerOpened}
        renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}
        onClose={closeAll}
      >
        <BottomDrawer.HandleWrapper>
          <BottomDrawer.Handle />
        </BottomDrawer.HandleWrapper>
        <BottomDrawer.Contents>
          <ul className="my-16 flex flex-col gap-16">
            {isMyComment ? (
              <>
                {onClickEditButton ? (
                  <li className="w-full">
                    <Button
                      className="w-full"
                      onClick={() => onClickEditButton(commentId)}
                    >
                      수정하기
                    </Button>
                  </li>
                ) : null}
                <li className="w-full">
                  <Button
                    className="w-full text-R-500"
                    theme="primary-line"
                    onClick={() => {
                      setModalType(MODAL_TYPE.DELETE);
                      setIsDrawerOpened(false);
                      setIsModalOpened(true);
                    }}
                  >
                    삭제하기
                  </Button>
                </li>
              </>
            ) : (
              <>
                <li className="flex w-full flex-col gap-8">
                  <Button
                    className="w-full text-Gray-900"
                    theme="primary-line"
                    onClick={() => {
                      setIsReportDrawerOpened(true);
                    }}
                  >
                    댓글 신고하기
                  </Button>
                </li>
              </>
            )}
          </ul>
        </BottomDrawer.Contents>
      </BottomDrawer>
      <Modal
        className="gap-16"
        opened={isModalOpened}
        onClose={() => setIsModalOpened(false)}
      >
        <h2 className="text-Gray-900 typo-body1">
          {
            {
              [MODAL_TYPE.DELETE]: '댓글을 삭제하시겠습니까?',
              [MODAL_TYPE.REPORT]: '댓글을 신고하시겠습니까?',
            }[modalType]
          }
        </h2>
        <footer className="mt-24 flex gap-8">
          <Button
            className="flex-1"
            theme="primary-line"
            onClick={() => {
              setIsModalOpened(false);
            }}
          >
            취소
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              switch (modalType) {
                case MODAL_TYPE.DELETE:
                  deleteCommentMutation.mutate({
                    path: {
                      welloCommentId: commentId,
                    },
                  });
                  break;
              }
            }}
          >
            {
              {
                [MODAL_TYPE.DELETE]: '삭제',
                [MODAL_TYPE.REPORT]: '신고',
              }[modalType]
            }
          </Button>
        </footer>
      </Modal>
      <BottomDrawer
        opened={isReportDrawerOpened}
        renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}
        onClose={() => setIsReportDrawerOpened(false)}
      >
        <BottomDrawer.HandleWrapper>
          <BottomDrawer.Handle />
        </BottomDrawer.HandleWrapper>
        <BottomDrawer.Contents>
          <form
            className="flex flex-col gap-30"
            onSubmit={(e) => {
              e.preventDefault();

              if (selectedReason) {
                postCommentReportMutation.mutate({
                  body: {
                    welloCommentId: commentId,
                    contents: selectedReason,
                  },
                });
              }
            }}
          >
            <h2 className="font-700 text-Gray-900 typo-body1">
              신고하는 이유를 선택해주세요.
            </h2>
            <Radio.Group
              className="flex-col gap-20"
              value={selectedReason}
              onChange={(value) => setSelectedReason(value)}
            >
              {REPORT_REASONS.map((reason) => (
                <Radio.Item
                  key={reason}
                  className="justify-start typo-body3"
                  value={reason}
                >
                  {reason}
                </Radio.Item>
              ))}
            </Radio.Group>
            <Button
              className="w-full"
              disabled={!selectedReason}
              loading={postCommentReportMutation.isPending}
              type="submit"
            >
              선택 완료
            </Button>
          </form>
        </BottomDrawer.Contents>
      </BottomDrawer>
    </>
  );
};
